import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { EVChargingCalculator } from '../RichCalculators';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function ElbilBudgetHovedstaden({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad koster det at have elbil i Hovedstaden i 2026?', answer: 'De største poster er opladning, forsikring, vægtafgift og service. Selve hjemmeopladningen kan komme helt ned omkring 2.500–4.500 kr./år for en typisk pendler i Hovedstaden (DK2), hvis du lader hjemme med spotpris uden tillæg, Saving Mode og laderabat. Hertil kommer faste udgifter som ved enhver bil.' },
    { question: 'Er det billigere at lade elbil hjemme end på offentlige ladere i København?', answer: 'Ja, markant. Hjemmeopladning (AC) til spotpris koster typisk en brøkdel af offentlig hurtigladning (DC), der ofte koster 4–7 kr./kWh. Derfor er en hjemmeladeboks den vigtigste investering for at holde elbil-budgettet nede i Hovedstaden.' },
    { question: 'Hvilken billig elbil-løsning passer til et stramt budget?', answer: 'Kombinationen af en prisvenlig ladeboks (NexBlue Edge 2 fra 3.499 kr.), en el-aftale uden tillæg (enkel kvartal fra 18 kr./md.), Saving Mode og 10 øre/kWh laderabat giver den laveste samlede pris. For mange dækker laderabatten reelt hele abonnementet.' },
    { question: 'Bor jeg i DK1 eller DK2 i Hovedstaden?', answer: 'Hovedstaden ligger i DK2 (Østdanmark), som dækker Sjælland, Lolland-Falster og Bornholm. Spotprisen følger DK2, men hos enkel energi betaler du spotpris + 0 øre tillæg uanset zone.' },
    { question: 'Kan jeg lade elbil i lejlighed i København?', answer: 'Hvis du har adgang til egen parkering med mulighed for ladeboks, ja. Bor du i etagebolig uden egen p-plads, er du oftere afhængig af ladestandere i nærheden eller på arbejdspladsen. enkel Saving Mode fungerer med egen ladeboks – ikke offentlige standere.' },
    { question: 'Hvor meget kan Saving Mode spare mig i Hovedstaden?', answer: 'Saving Mode flytter automatisk opladningen til de billigste timer. Historiske data viser besparelser på 15–30% på den variable del af opladningen. For en pendler i Hovedstaden kan det betyde flere tusinde kroner over et år – men besparelsen er ikke garanteret og afhænger af spotprisudsving.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'budget', label: 'Budget-oversigt' },
    { id: 'opladning', label: 'Opladning billigst' },
    { id: 'beregner', label: 'Beregn dit budget' },
    { id: 'loesning', label: 'Billig løsning' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Elbil på budget i Hovedstaden', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Elbil på budget i Hovedstaden 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Køberguide · Hovedstaden (DK2) · 2026"
        h1="Elbil på budget i Hovedstaden 2026"
        intro="Vil du køre elbil i Hovedstaden uden at sprænge budgettet? Her er det fulde overblik over, hvad det koster – og hvordan du holder opladningen så billig som muligt med spotpris uden tillæg, Saving Mode og laderabat."
        badges={['Hovedstaden = DK2', 'Spotpris + 0 øre', 'Laderabat 10 øre/kWh']}
        updated={updated}
        image="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '~2.500–4.500 kr.', label: 'opladning/år for typisk pendler' },
          { value: 'DK2', label: 'Hovedstadens prisområde' },
          { value: '18 kr./md.', label: 'fra (enkel kvartal)' },
          { value: '15–30%', label: 'historisk Saving Mode-besparelse' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad koster en elbil på budget i Hovedstaden?">
        <AnswerBox question="Hvad koster det at have elbil i Hovedstaden i 2026?">
          De største løbende poster er <strong>opladning, forsikring, afgift og service</strong>. Selve hjemmeopladningen kan komme ned omkring
          <strong> 2.500–4.500 kr./år</strong> for en typisk pendler i Hovedstaden (DK2), hvis du lader hjemme med spotpris uden tillæg, Saving Mode
          og 10 øre/kWh laderabat på opladning via Saving Mode. Det er langt billigere end offentlig hurtigladning, der ofte koster 4–7 kr./kWh.
        </AnswerBox>
        <p>
          Hovedstaden ligger i prisområde DK2 (Østdanmark). For at holde elbil-budgettet nede handler det især om at lade rigtigt: hjemme,
          i de billige timer, og hos et selskab uden tillæg. Det er her, du kan spare flest penge – uden at gå på kompromis med komforten.
        </p>
      </ProseSection>

      <ProseSection id="budget" heading="Sådan ser et elbil-budget typisk ud" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Typiske løbende udgifter ved elbil"
            columns={[{ name: 'Hvad', highlight: true }, { name: 'Bemærkning' }]}
            rows={[
              { label: 'Hjemmeopladning', values: ['Den post du bedst kan styre', 'Spotpris + Saving Mode + laderabat'] },
              { label: 'Offentlig ladning', values: ['Dyrere – undgå hvis muligt', 'Ofte 4–7 kr./kWh'] },
              { label: 'Forsikring', values: ['Fast', 'Som ved benzinbil'] },
              { label: 'Grøn ejerafgift', values: ['Fast', 'Lav for elbiler'] },
              { label: 'Service', values: ['Lavere end fossilbil', 'Færre sliddele'] },
            ]}
          />
        </div>
        <p>Den eneste post, du for alvor selv kan flytte på, er opladningen – og netop her gør valget af el-aftale og ladestrategi en stor forskel.</p>
      </ProseSection>

      <ProseSection id="opladning" heading="Hjemmeopladning er nøglen til et lavt budget">
        <FactGrid columns={3} facts={[
          { value: 'AC hjemme', label: 'Billigst', sub: 'Spotpris time for time' },
          { value: 'DC offentlig', label: 'Dyrest', sub: 'Ofte 4–7 kr./kWh' },
          { value: '10 øre/kWh', label: 'Laderabat hjemme', sub: 'Via enkel app' },
        ]} />
        <p className="mt-6">
          Når du lader hjemme via enkel, kombinerer du tre fordele: den lave spotpris, Saving Mode der vælger de billigste timer, og en
          laderabat på 10 øre/kWh på de kWh, der lades via Saving Mode i enkel-appen med kompatibel ladeboks og bil. For mange pendlere i Hovedstaden dækker laderabatten reelt hele
          el-abonnementet.
        </p>
      </ProseSection>

      <ProseSection id="beregner" heading="Beregn dit eget opladningsbudget" alt>
        <div className="my-2"><EVChargingCalculator defaultKm={15000} /></div>
      </ProseSection>

      <ProseSection id="loesning" heading="Den billigste elbil-løsning til Hovedstaden">
        <p>Til et stramt budget anbefaler vi den prisvenlige NexBlue Edge 2 sammen med enkel kvartal. Vil du fremtidssikre med V2G, vælg Zaptec Go 2:</p>
        <div className="grid sm:grid-cols-2 gap-4 my-6">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="Billigst – ideel til budget" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="Fremtidssikret · V2G" />
        </div>
        <AnswerBox>
          Med enkel kvartal (18 kr./md. = 216 kr./år) og en laderabat på ca. 792 kr./år ved 7.920 kWh hjemmeopladning via Saving Mode, er din el-aftale reelt
          <strong> gratis</strong> – overskuddet modregnes resten af din elregning. Det er sådan, du holder elbil-budgettet i Hovedstaden helt nede.
        </AnswerBox>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/sammenlign/hvad-koster-opladning-hjemme-hovedstaden', label: 'Hvad koster opladning hjemme?' },
        { path: '/elbil', label: 'Elbil-løsning' },
        { path: '/portal/smart-charge', label: 'Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Kør elbil billigt i Hovedstaden" subtext="Spotpris uden tillæg, Saving Mode og laderabat. Fra 18 kr./md., ingen binding." />
      <SEODisclaimer page={page} />
    </article>
  );
}